import { z } from 'zod';

import type { LibraryItemView } from '@/lib/api/generated/model';

export const LIBRARY_TABS = ['workflow', 'agent', 'skill'] as const;
export type LibraryTab = (typeof LIBRARY_TABS)[number];

/** Which part of the catalog is on screen (spec 105 FR-001). */
export const LIBRARY_STATES = [
  'all',
  'installed',
  'updates',
  'shared',
] as const;
export type LibraryStateFilter = (typeof LIBRARY_STATES)[number];

/** Which item the drawer is showing, as the address writes it (spec 108 US2). */
export interface ItemRef {
  kind: LibraryTab;
  key: string;
}

/** An item as the address writes it: `kind/key`, e.g. `agent/reviewer`. */
export function itemRef(item: Pick<LibraryItemView, 'kind' | 'key'>): string {
  return `${item.kind}/${item.key}`;
}

/**
 * The pair an `item` parameter names, or nothing (spec 108 FR-002).
 *
 * Total on purpose. An address is text a person can edit, and this is the only
 * place that decides whether what they typed is a well-formed pair — so it
 * answers `undefined` rather than throwing, and the page shows no drawer. That
 * is the same fall-back `tab` and `state` already have.
 *
 * It does not decide whether the pair names something the library holds; only
 * the catalog knows that, and the catalog is not loaded when the route
 * validates its search parameters. A well-formed pair for an item that has gone
 * is therefore handled further along, and in exactly the same way.
 *
 * The first slash separates. A key is a folder name in the library's own layout
 * and cannot contain one, so nothing is lost by splitting there.
 */
export function parseItemRef(raw: string | undefined): ItemRef | undefined {
  if (!raw) return undefined;
  const slash = raw.indexOf('/');
  if (slash <= 0) return undefined;
  const kind = raw.slice(0, slash);
  const key = raw.slice(slash + 1);
  if (!key) return undefined;
  if (!(LIBRARY_TABS as readonly string[]).includes(kind)) return undefined;
  return { kind: kind as LibraryTab, key };
}

/**
 * The Library's address: which tab is open, what was typed into search, how
 * much of the catalog is on screen, and which item's drawer is open.
 *
 * All four are shareable and survive a reload (spec 104 FR-005, spec 105
 * FR-004, spec 108 FR-001), so they live in the route rather than in component
 * state. Unrecognised values fall back to the defaults rather than failing the
 * route, as the files page does — a search parameter is user-editable text.
 * `state` defaulting to `all` is also what keeps a link written before the
 * narrowing existed behaving exactly as it did, and `item` defaulting to empty
 * does the same for every address written before the drawer was shareable.
 *
 * `item` is a plain string here rather than a refined pair. Zod's job is to stop
 * a mangled address failing the route; whether `agent/reviewer` is a real item
 * is the catalog's question, and `parseItemRef` above says why that split is
 * deliberate.
 */
export const librarySearchSchema = z.object({
  tab: z.enum(LIBRARY_TABS).catch('workflow').default('workflow'),
  q: z.string().catch('').default(''),
  state: z.enum(LIBRARY_STATES).catch('all').default('all'),
  item: z.string().catch('').default(''),
});

export type LibrarySearch = z.infer<typeof librarySearchSchema>;
